import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";

interface ProtectedRouteProps {
	children: React.ReactNode;
	requiredRoles?: ("super_admin" | "admin" | "support")[];
}

/**
 * ProtectedRoute wrapper component
 * Redirects to login if not authenticated
 * Optionally checks for required roles
 */
export function ProtectedRoute({
	children,
	requiredRoles,
}: ProtectedRouteProps) {
	const { isAuthenticated, isLoading, admin } = useAuthStore();
	const location = useLocation();

	// Show loading while checking auth
	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-100">
				<div className="flex flex-col items-center gap-4">
					<div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-600" />
					<p className="text-gray-600">Loading...</p>
				</div>
			</div>
		);
	}

	// Redirect to login if not authenticated
	if (!isAuthenticated) {
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	// Check role requirements if specified
	if (requiredRoles && requiredRoles.length > 0 && admin) {
		if (!requiredRoles.includes(admin.role)) {
			return (
				<div className="min-h-screen flex items-center justify-center bg-gray-100">
					<div className="bg-white p-8 rounded-lg shadow-md max-w-md text-center">
						<div className="text-red-500 text-5xl mb-4">403</div>
						<h2 className="text-2xl font-bold text-gray-800 mb-2">
							Access Denied
						</h2>
						<p className="text-gray-600 mb-6">
							You don't have permission to access this page.
						</p>
						<p className="text-sm text-gray-500">
							Required role: {requiredRoles.join(" or ")}
						</p>
						<p className="text-sm text-gray-500">Your role: {admin.role}</p>
					</div>
				</div>
			);
		}
	}

	return <>{children}</>;
}

/**
 * Higher-order component version of ProtectedRoute
 */
export function withProtectedRoute<P extends object>(
	Component: React.ComponentType<P>,
	requiredRoles?: ("super_admin" | "admin" | "support")[],
) {
	return function ProtectedComponent(props: P) {
		return (
			<ProtectedRoute requiredRoles={requiredRoles}>
				<Component {...props} />
			</ProtectedRoute>
		);
	};
}
